import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, getProductsCatalog, normalizePaginatedItems, CATALOG_MAX_LIMIT, createMockupTask, getProductDetails, getSavedAddresses, saveAddress, getProfile, updateProfile, catalogItemKey, parseCatalogItemKey, persistGuestEmail, readGuestEmail, guestEmailParams } from '../apiClient';
import FeedbackModal from './FeedbackModal';

function rawVariantIsOrderable(raw) {
  if (!raw || typeof raw !== 'object') return true;
  if (raw.is_enabled === false) return false;
  if (raw.is_available === false) return false;
  if (raw.in_stock === false) return false;
  return true;
}

/** Printful / Printify list rows: { size, color } or title "S / White". */
function normalizeVariantList(variants) {
  if (!Array.isArray(variants)) return [];
  return variants.filter((raw) => rawVariantIsOrderable(raw)).map((raw) => {
    if (raw && typeof raw.size === 'string' && raw.color != null && raw.color !== '') {
      return { size: String(raw.size), color: String(raw.color), raw };
    }
    const title = (raw?.title || '').toString().trim();
    const parts = title.split(/\s*\/\s*/).map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      return { size: parts[0], color: parts[parts.length - 1], raw };
    }
    if (title) return { size: 'One size', color: title, raw };
    return { size: 'Standard', color: 'Default', raw };
  });
}

function sellingPriceNumber(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

const PrintOptions = ({ image, userFullName, onProductImageSelected, onProductChange, selectedProduct, position, size, rotation, printArea, onMockupGenerated, shippingAddress: initialShippingAddress, productDetails: initialProductDetails, placements = [], setPlacements, displayableImages = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mockupToOrder } = location.state || {}; // Get mockup data from navigation state
  const [currentPrintImage, setCurrentPrintImage] = useState(image); // New state for the image to be printed/mocked
  const [productsCatalog, setProductsCatalog] = useState([]);

  // Sync when parent passes a new image (e.g. from navigation state)
  useEffect(() => {
    if (image) setCurrentPrintImage(image);
  }, [image]);

  const isDataUrl = (url) => typeof url === 'string' && url.trim().toLowerCase().startsWith('data:');
  // Multi-placement: need valid images for all placements; single: use currentPrintImage
  const cannotCreateMockup = placements?.length > 0
    ? placements.some((p) => !p.image?.id && isDataUrl(p.image?.image_url || p.image?.url))
    : !currentPrintImage?.id && isDataUrl(currentPrintImage?.image_url);

  const [selectedModel, setSelectedModel] = useState(
    selectedProduct ? catalogItemKey(selectedProduct) : '',
  );
  const [detailedProductData, setDetailedProductData] = useState(initialProductDetails);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColorName, setSelectedColorName] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('Printful'); // Default; chosen from product source when available
  const [quantity, setQuantity] = useState(1);
  const [shippingFirstName, setShippingFirstName] = useState(userFullName ? userFullName.split(' ')[0] : '');
  const [shippingLastName, setShippingLastName] = useState(userFullName ? userFullName.split(' ')[1] : '');
  const [shippingAddress1, setShippingAddress1] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingZip, setShippingZip] = useState('');
  const [shippingCountryCode, setShippingCountryCode] = useState('US');
  const [shippingEmail, setShippingEmail] = useState(() => readGuestEmail());
  const [shippingPhone, setShippingPhone] = useState('');
  const [shippingState, setShippingState] = useState('');
  const [selectedPlacement, setSelectedPlacement] = useState('');
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(null); // Which saved address is selected for shipping
  const [saveAddressToProfile, setSaveAddressToProfile] = useState(false);
  const [dynamicPlacementData, setDynamicPlacementData] = useState({}); // New state for dynamic placement inputs
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [mockupStatus, setMockupStatus] = useState('');
  const [mockupMessage, setMockupMessage] = useState('');
  const [mockupImageUrl, setMockupImageUrl] = useState('');

  useEffect(() => {
    if (detailedProductData && selectedPlacement) {
      const placementFile = detailedProductData.files.find(file => file.type === selectedPlacement);
      if (placementFile && placementFile.options) {
        const initialPlacementData = {};
        placementFile.options.forEach(option => {
          if (!option.id.startsWith('thread_colors_')) {
            initialPlacementData[option.id] = option.default !== undefined ? option.default : '';
          }
        });
        setDynamicPlacementData(initialPlacementData);
      } else {
        setDynamicPlacementData({});
      }
    }
  }, [detailedProductData, selectedPlacement]);

  // Sync selectedPlacement from first placement when using multi-placement
  useEffect(() => {
    if (placements?.length > 0 && placements[0]?.placement) {
      setSelectedPlacement(placements[0].placement);
    }
  }, [placements?.[0]?.placement]);

  useEffect(() => {
    if (initialShippingAddress) {
      setShippingFirstName(initialShippingAddress.first_name || '');
      setShippingLastName(initialShippingAddress.last_name || '');
      setShippingAddress1(initialShippingAddress.address1 || '');
      setShippingCity(initialShippingAddress.city || '');
      setShippingZip(initialShippingAddress.zip || '');
      setShippingCountryCode(initialShippingAddress.country_code || 'US');
      setShippingEmail(initialShippingAddress.email || '');
      setShippingPhone(initialShippingAddress.phone || '');
      setShippingState(initialShippingAddress.state || '');
    } else if (userFullName) {
      const nameParts = userFullName.split(' ');
      setShippingFirstName(nameParts[0] || '');
      setShippingLastName(nameParts.slice(1).join(' ') || '');
    }
  }, [initialShippingAddress, userFullName]);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const Session = (await import('supertokens-auth-react/recipe/session')).default;
        if (!(await Session.doesSessionExist())) return;
        const data = await getSavedAddresses();
        setSavedAddresses(data.addresses || []);
      } catch (err) {
        // User may not be logged in or no addresses
      }
    };
    fetchAddresses();
  }, []);

  const applySavedAddress = (addr, index) => {
    if (!addr) return;
    setShippingFirstName(addr.first_name || '');
    setShippingLastName(addr.last_name || '');
    setShippingAddress1(addr.address1 || '');
    setShippingCity(addr.city || '');
    setShippingState(addr.state || '');
    setShippingZip(addr.zip || '');
    setShippingCountryCode(addr.country_code || 'US');
    setShippingEmail(addr.email || '');
    setShippingPhone(addr.phone || '');
    setSelectedAddressIndex(index);
  };

  const handleDetectLocation = async () => {
    try {
      const res = await fetch('https://ipapi.co/json/');
      const data = await res.json();
      if (data.country_code) setShippingCountryCode(data.country_code.toUpperCase());
      if (data.city) setShippingCity(data.city);
      if (data.region) setShippingState(data.region);
      if (data.postal) setShippingZip(data.postal);
      setMessage('Location detected. Please complete the rest of the form.');
    } catch (err) {
      setMessage('Could not detect location. Please fill in manually.');
    }
  };

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [displayPrice, setDisplayPrice] = useState(0);
  const [availability, setAvailability] = useState('');
  const [selectedStitchColor, setSelectedStitchColor] = useState('');
  const [generatedMockupId, setGeneratedMockupId] = useState(null); // New state for generated mockup ID

  // Debugging logs
  if (process.env.NODE_ENV === 'development') {
    console.log("PrintOptions: productsCatalog", productsCatalog);
    console.log("PrintOptions: selectedModel", selectedModel);
  }


  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // API returns { items, total, limit, offset }; dropdown needs an array (and a high limit for full list).
        const data = await getProductsCatalog({ limit: CATALOG_MAX_LIMIT, offset: 0 });
        setProductsCatalog(normalizePaginatedItems(data));
      } catch (error) {
        console.error('Error fetching products catalog:', error);
        setMessage('Failed to load product options.');
      }
    };
    fetchProducts(); // Catalog for model dropdown (paginated API, normalized to items[])

    if (mockupToOrder) {
      // ... (existing logic for mockupToOrder)
    } else if (initialProductDetails) {
      setDetailedProductData(initialProductDetails);
      setSelectedModel(catalogItemKey(initialProductDetails));
      onProductImageSelected(initialProductDetails.image);
      setDisplayPrice(sellingPriceNumber(initialProductDetails.selling_price) ?? 0);
      const iv = normalizeVariantList(initialProductDetails.variants);
      if (iv.length) {
        const sz = iv[0].size;
        setSelectedSize(sz);
        const cols = [...new Set(iv.filter((x) => x.size === sz).map((x) => x.color))];
        setSelectedColorName(cols[0] || '');
      }
      let availabilityInfo = initialProductDetails.origin_country ? `Sourced from: ${initialProductDetails.origin_country}` : 'Availability not specified';
      if (initialProductDetails.description) {
        const availabilityMatch = initialProductDetails.description.match(/Important: (.*)/);
        if (availabilityMatch && availabilityMatch[1]) {
          availabilityInfo = availabilityMatch[1];
        }
      }
      setAvailability(availabilityInfo);
      const facilities = Object.keys(initialProductDetails.provider_mappings);
      const defaultFacility = facilities.length > 0 ? facilities[0] : 'Printful';
      setSelectedFacility(defaultFacility);
    } else if (image) {
      setCurrentPrintImage(image);
    }
  }, [initialProductDetails, mockupToOrder, image]);

  const handleModelChange = async (e) => {
    const key = e.target.value;
    setSelectedModel(key);
    setSelectedSize('');
    setSelectedColorName('');
    setSelectedPlacement('');

    if (key) {
      const { id: modelId, printing_provider } = parseCatalogItemKey(key);
      if (modelId == null) return;
      try {
        const details = await getProductDetails(modelId, { printing_provider });
        setDetailedProductData(details);
        onProductImageSelected(details.image);
        setDisplayPrice(sellingPriceNumber(details.selling_price) ?? 0);
        const nv = normalizeVariantList(details.variants);
        if (nv.length > 0) {
          const sz = nv[0].size;
          setSelectedSize(sz);
          const cols = [...new Set(nv.filter((x) => x.size === sz).map((x) => x.color))];
          setSelectedColorName(cols[0] || '');
        }
        if (onProductChange) {
          const catalogProduct = productsCatalog.find((p) => catalogItemKey(p) === key);
          onProductChange({
            id: details.id,
            name: details.name ?? catalogProduct?.name,
            image: details.image,
            selling_price: details.selling_price,
            coverage_locations: catalogProduct?.coverage_locations ?? details.coverage_locations,
            avg_fulfillment_time: catalogProduct?.avg_fulfillment_time ?? details.avg_fulfillment_time,
            catalog_provider: details.catalog_provider ?? catalogProduct?.catalog_provider,
          });
        }

        let availabilityInfo = details.origin_country ? `Sourced from: ${details.origin_country}` : 'Availability not specified';
        if (details.description) {
          const availabilityMatch = details.description.match(/Important: (.*)/);
          if (availabilityMatch && availabilityMatch[1]) {
            availabilityInfo = availabilityMatch[1];
          }
        }
        setAvailability(availabilityInfo);
        // Set facility from product source (e.g. first provider); default Printful
        const facilities = Object.keys(details.provider_mappings);
        const defaultFacility = facilities.length > 0 ? facilities[0] : 'Printful';
        setSelectedFacility(defaultFacility);
      } catch (error) {
        console.error('Error fetching detailed product data on model change:', error);
        setMessage('Failed to load detailed product information for selected model.');
        setDetailedProductData(null);
        onProductImageSelected('');
        setDisplayPrice(0);
        setSelectedFacility('');
      }
    } else {
      setDetailedProductData(null);
      onProductImageSelected('');
      setDisplayPrice(0);
      setSelectedFacility('Printful');
    }
  };

  const selectedModelId = parseCatalogItemKey(selectedModel).id;

  const handleSizeChange = (e) => {
    const sz = e.target.value;
    setSelectedSize(sz);
    if (detailedProductData?.variants && sz) {
      const nv = normalizeVariantList(detailedProductData.variants);
      const cols = [...new Set(nv.filter((x) => x.size === sz).map((x) => x.color))];
      setSelectedColorName(cols[0] || '');
    }
  };

  const handleColorChange = (e) => {
    const newColorName = e.target.value;
    setSelectedColorName(newColorName);

    if (detailedProductData && newColorName && selectedSize) {
      const nv = normalizeVariantList(detailedProductData.variants);
      const row = nv.find((x) => x.size === selectedSize && x.color === newColorName);
      const raw = row?.raw;
      const vimg = raw?.image || raw?.preview_url;
      if (vimg) {
        onProductImageSelected(vimg);
      } else {
        onProductImageSelected(detailedProductData.image);
      }
    }
  };

  const normalizedVariants = useMemo(
    () => normalizeVariantList(detailedProductData?.variants),
    [detailedProductData],
  );

  const availableSizes = useMemo(
    () => [...new Set(normalizedVariants.map((v) => v.size))].filter(Boolean),
    [normalizedVariants],
  );

  const availableColors = useMemo(() => {
    if (!selectedSize) return [];
    return [...new Set(normalizedVariants.filter((v) => v.size === selectedSize).map((v) => v.color))].filter(Boolean);
  }, [normalizedVariants, selectedSize]);

  const availablePlacements = useMemo(() => {
    if (!detailedProductData || !detailedProductData.files) return [{ type: 'default', title: 'Front Print' }];
    // Show every print area Printful/Printify exposes — do not duplicate the catalog facet allowlist here
    // (it hid valid types like embroidery_chest_center when lists drifted or builds were stale).
    const placements = detailedProductData.files
      .filter((file) => file.type && file.type !== 'mockup' && file.type !== 'preview')
      .map((file) => ({ type: file.type, title: file.title || file.type }));

    if (placements.length === 0) {
      return [{ type: 'default', title: 'Front Print' }];
    }
    return placements;
  }, [detailedProductData]);

  const handleSendToPrint = async () => {
    if (!selectedModel || !selectedSize || !selectedColorName || !shippingAddress1 || !shippingCity || !shippingZip || !shippingCountryCode || !shippingState || !selectedPlacement) {
      setMessage('Please fill in all required fields (T-shirt Model, Size, Color, Shipping Address including Address Line 1 and State, and Image Placement).');
      setLoading(false);
      return;
    }

    setLoading(true);
    setMessage('');

    const shippingAddress = {
      first_name: shippingFirstName,
      last_name: shippingLastName,
      address1: shippingAddress1,
      city: shippingCity,
      state: shippingState, // Add state
      zip: shippingZip,
      country_code: shippingCountryCode,
      email: shippingEmail,
      phone: shippingPhone,
    };

    try {
      const response = await api.post('/print-order', {
        image_url: currentPrintImage.image_url,
        prompt: currentPrintImage.prompt,
        style: currentPrintImage.style,
        tshirt_color: currentPrintImage.tshirt_color || '', // Ensure this is always a string
        printing_facility_name: selectedFacility,
        quantity: quantity,
        shipping_address: shippingAddress,
        tshirt_model: selectedModelId,
        tshirt_size: selectedSize,
        tshirt_selected_color: selectedColorName,
        image_placement: selectedPlacement, // Add selected image placement
        stitch_color: selectedStitchColor, // Add selected stitch color
      });
      if (process.env.NODE_ENV === 'development') console.log("Sending to print with stitch_color:", selectedStitchColor);
      setMessage(`Order placed successfully with ${selectedFacility}! Order ID: ${response.data.order_id}`);
    } catch (error) {
      console.error('Error sending to print:', error);
      setMessage(`Failed to place order: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!selectedModel || !selectedSize || !selectedColorName || !shippingAddress1 || !shippingCity || !shippingZip || !shippingCountryCode || !shippingState || !selectedPlacement) {
      setMessage('Please fill in all required fields (T-shirt Model, Size, Color, Shipping Address including Address Line 1 and State, and Image Placement) before proceeding to payment.');
      return;
    }

    const shippingAddress = {
      first_name: shippingFirstName,
      last_name: shippingLastName,
      address1: shippingAddress1,
      city: shippingCity,
      state: shippingState,
      zip: shippingZip,
      country_code: shippingCountryCode,
      email: shippingEmail,
      phone: shippingPhone,
    };
    persistGuestEmail(shippingEmail);

    const paymentDetails = {
      mockupId: generatedMockupId,
      quantity: quantity,
      totalAmount: parseFloat((displayPrice * quantity).toFixed(2)),
      currency: 'usd',
      shippingAddress: shippingAddress,
    };

    navigate('/payment', { state: paymentDetails });
  };

  const pollMockupStatus = async (mockupId) => {
    let attempts = 0;
    // First poll is immediate; then 2s for early checks, then 5s (~same total window as old 20×5s).
    const maxAttempts = 36;
    const earlyPolls = 12;
    const earlyDelayMs = 2000;
    const steadyDelayMs = 5000;
    let timeoutId = null;

    const stopPolling = () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const waitingMessages = [
      'Creating your mockup...',
      'Adding the finishing touches...',
      'Our printers are warming up...',
      'Making it look amazing...',
      'Quality takes time...',
      'Almost there...',
      'Just a moment longer...',
      'Your design is taking shape...',
      'Good things come to those who wait...',
      'Almost ready to show you...',
    ];

    const scheduleNext = () => {
      const delayMs = attempts < earlyPolls ? earlyDelayMs : steadyDelayMs;
      timeoutId = setTimeout(() => {
        void runPoll();
      }, delayMs);
    };

    const runPoll = async () => {
      attempts += 1;
      try {
        const response = await api.get(`/mockups/${mockupId}/status`, { params: guestEmailParams() });
        const { status } = response.data;

        if (status.startsWith('polling_attempt_') || (status !== 'completed' && status !== 'failed')) {
          const msgIndex = (attempts - 1) % waitingMessages.length;
          setMockupMessage(waitingMessages[msgIndex]);
        }

        if (status === 'completed') {
          setMockupStatus('completed');
          setMockupMessage('Mockup generated successfully! You can now proceed to payment.');
          stopPolling();
          const mockupDetails = await api.get(`/mockups/${mockupId}`, { params: guestEmailParams() });
          onMockupGenerated(mockupDetails.data);
          setGeneratedMockupId(mockupId);
          setDisplayPrice(sellingPriceNumber(mockupDetails.data.price) ?? 0);
          setMockupImageUrl(mockupDetails.data.mockup_image_url);
          if (saveAddressToProfile && shippingFirstName && shippingLastName && shippingAddress1 && shippingCity && shippingState && shippingZip && shippingCountryCode) {
            try {
              await saveAddress({
                first_name: shippingFirstName, last_name: shippingLastName, address1: shippingAddress1,
                city: shippingCity, state: shippingState, zip: shippingZip, country_code: shippingCountryCode,
                email: shippingEmail || undefined, phone: shippingPhone || undefined
              });
              setSaveAddressToProfile(false);
            } catch (e) {
              console.warn('Could not save address to profile:', e);
            }
          }
          if (shippingFirstName && shippingLastName) {
            try {
              const Session = (await import('supertokens-auth-react/recipe/session')).default;
              if (await Session.doesSessionExist()) {
                const profile = await getProfile();
                if (!profile?.full_name?.trim()) {
                  await updateProfile({ full_name: `${shippingFirstName} ${shippingLastName}`.trim() });
                }
              }
            } catch (e) {
              console.warn('Could not update profile name:', e);
            }
          }
          return;
        }

        if (status === 'failed') {
          setMockupStatus('failed');
          setMockupMessage('Mockup generation failed. Please try again.');
          stopPolling();
          return;
        }

        if (status === 'scheduled_for_retry') {
          setMockupStatus('timeout');
          setMockupMessage("Hmmm this is taking more than expected, please return back later in your mockups page your will find the generated mockup");
          stopPolling();
          return;
        }

        if (attempts >= maxAttempts && status !== 'completed') {
          setMockupStatus('timeout');
          setMockupMessage("Hmmm this is taking more than expected, please return back later in your mockups page your will find the generated mockup");
          stopPolling();
          return;
        }

        scheduleNext();
      } catch (error) {
        console.error('Error polling mockup status:', error);
        setMockupStatus('failed');
        setMockupMessage('An error occurred while checking the mockup status.');
        stopPolling();
      }
    };

    void runPoll();
  };

  const handleGenerateMockup = async () => {
    if (cannotCreateMockup) {
      setMessage('This image is not from the server yet. Go back to Design, wait for your upload to finish, then click "Use This Image" again before choosing a product.');
      return;
    }
    const usePlacements = placements?.length > 0 && setPlacements;
    const effectivePlacement = usePlacements ? placements[0]?.placement : selectedPlacement;
    if (!selectedModel || !selectedSize || !selectedColorName || !shippingFirstName || !shippingLastName || !shippingAddress1 || !shippingCity || !shippingZip || !shippingCountryCode || !shippingState || !effectivePlacement || !shippingEmail || !shippingPhone) {
      setMessage('Please fill in all required fields, including a shipping address, email, and phone. Printful needs a valid address to ship.');
      return;
    }
    persistGuestEmail(shippingEmail);

    if (
      selectedFacility === 'Printful' &&
      detailedProductData?.options?.some((o) => o.id === 'stitch_color') &&
      !String(selectedStitchColor || '').trim()
    ) {
      setMessage('This product requires a stitch color (white or black). Please select one.');
      return;
    }

    setLoading(true);
    setIsFeedbackModalOpen(true);
    setMockupStatus('processing');
    setMockupMessage('Creating your mockup...');

    const baseData = {
      tshirt_model: selectedModelId,
      tshirt_size: selectedSize,
      tshirt_selected_color: selectedColorName,
      stitch_color: selectedStitchColor,
      printing_facility_name: selectedFacility,
      shipping_address: {
        first_name: shippingFirstName,
        last_name: shippingLastName,
        address1: shippingAddress1,
        city: shippingCity,
        state: shippingState,
        zip: shippingZip,
        country_code: shippingCountryCode,
        email: shippingEmail,
        phone: shippingPhone
      },
    };

    const mockupData = usePlacements
      ? {
          ...baseData,
          placements: placements.map((p) => ({
            placement: p.placement,
            image_url: p.image?.image_url || p.image?.url,
          })),
          placement: {
            x: Math.round(((position.x / 100) * printArea.width) - (((size / 100) * printArea.width) / 2)),
            y: Math.round(((position.y / 100) * printArea.height) - (((size / 100) * printArea.height) / 2)),
            width: Math.round((size / 100) * printArea.width),
            height: Math.round((size / 100) * printArea.height),
            rotation: rotation,
          },
        }
      : {
          ...baseData,
          image_id: currentPrintImage.id,
          image_url: currentPrintImage.image_url,
          image_placement: selectedPlacement,
          placement: {
            x: Math.round(((position.x / 100) * printArea.width) - (((size / 100) * printArea.width) / 2)),
            y: Math.round(((position.y / 100) * printArea.height) - (((size / 100) * printArea.height) / 2)),
            width: Math.round((size / 100) * printArea.width),
            height: Math.round((size / 100) * printArea.height),
            rotation: rotation,
          },
          ...(selectedPlacement && selectedPlacement.startsWith('embroidery_')
            ? { auto_thread_color: true }
            : {}
          ),
        };

    console.log("Data sent to createMockupTask:", mockupData);

    try {
      const response = await createMockupTask(mockupData);
      if (process.env.NODE_ENV === 'development') {
        console.log("Create Mockup Response (full):", response);
        console.log("Create Mockup Response (data):", response.data);
      }
      const { mockup_id } = response.data;
      pollMockupStatus(mockup_id);
    } catch (error) {
      console.error('Error generating mockup:', error);
      console.error('Error response:', error.response);
      setMockupStatus('failed');
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((e) => e?.msg || JSON.stringify(e)).filter(Boolean).join('; ')
          : detail?.message || (detail && typeof detail === 'object' ? JSON.stringify(detail) : null);
      setMockupMessage(`Failed to start mockup generation: ${msg || error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-auto pt-4 border-t border-gray-200">
      {isFeedbackModalOpen && (
        <FeedbackModal
          status={mockupStatus}
          message={mockupMessage}
          onClose={() => setIsFeedbackModalOpen(false)}
          onProceedToPayment={handleProceedToPayment}
          mockupImageUrl={mockupImageUrl}
        />
      )}
      <div className="mb-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
        <p className="text-sm text-blue-700">
          This is just a mockup creation. Your address details are needed to calculate the shipping time.
        </p>
      </div>
      <h3 className="text-xl font-semibold mb-4 text-gray-800">Print Options</h3>

      <div className="p-6 border border-gray-200 rounded-lg shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-6">
          {/* Column 1: Product Selection - Dropdown (main product visual is in Selected Product panel above) */}
          <div>
            <div className="mb-4">
              <label htmlFor="tshirtModel" className="block text-sm font-medium text-gray-700 mb-1">Product model:</label>
              <select
                id="tshirtModel"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={selectedModel}
                onChange={handleModelChange}
              >
                <option value="">Select a model</option>
                {productsCatalog.map((model) => (
                  <option key={catalogItemKey(model)} value={catalogItemKey(model)}>{model.name}</option>
                ))}
              </select>
            </div>

            {detailedProductData && (
              <>
                <div className="mb-4">
                  <label htmlFor="tshirtSize" className="block text-sm font-medium text-gray-700 mb-1">Size:</label>
                  <select
                    id="tshirtSize"
                    className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                    value={selectedSize}
                    onChange={handleSizeChange}
                  >
                    <option value="">Select a size</option>
                    {availableSizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="tshirtColor" className="block text-sm font-medium text-gray-700 mb-1">Color:</label>
                  <select
                    id="tshirtColor"
                    className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                    value={selectedColorName}
                    onChange={handleColorChange}
                  >
                    <option value="">Select a color</option>
                    {availableColors.map((color) => (
                      <option key={color} value={color}>{color}</option>
                    ))}
                  </select>
                </div>

                {/* New Stitch Color Selection */}

                {detailedProductData && detailedProductData.options && detailedProductData.options.some(opt => opt.id === 'stitch_color') && (() => {
                  const scOpt = detailedProductData.options.find((opt) => opt.id === 'stitch_color');
                  const vals = scOpt?.values;
                  return (
                  <div className="mb-4">
                    <label htmlFor="stitchColor" className="block text-sm font-medium text-gray-700 mb-1">Stitch color:</label>
                    <select
                      id="stitchColor"
                      className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                      value={selectedStitchColor}
                      onChange={(e) => setSelectedStitchColor(e.target.value)}
                    >
                      <option value="">Select stitch color</option>
                      {Array.isArray(vals) && vals.map((v) => (
                        <option key={v.id} value={String(v.id)}>{v.title || v.name || v.id}</option>
                      ))}
                      {vals && !Array.isArray(vals) && typeof vals === 'object' && Object.entries(vals).map(([id, name]) => (
                        <option key={id} value={String(id)}>{String(name)}</option>
                      ))}
                    </select>
                  </div>
                  );
                })()}

                {/* Multi-placement: placement cards, or single placement dropdown */}
                {detailedProductData && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Image Placement{placements?.length > 1 ? 's' : ''}:</label>
                    {placements?.length > 0 && setPlacements ? (
                      <div className="space-y-3">
                        {placements.map((p, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
                            <select
                              className="flex-1 min-w-0 p-2 border border-gray-300 rounded text-sm"
                              value={p.placement}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPlacements((prev) => prev.map((pl, i) => (i === idx ? { ...pl, placement: v } : pl)));
                              }}
                              disabled={!selectedSize || !selectedColorName}
                            >
                              {availablePlacements.map((ap) => (
                                <option key={ap.type} value={ap.type} disabled={placements.some((pl, i) => i !== idx && pl.placement === ap.type)}>{ap.title}</option>
                              ))}
                            </select>
                            <img src={p.image?.image_url || p.image?.url} alt="" className="w-12 h-12 object-cover rounded border" />
                            <select
                              className="p-2 border border-gray-300 rounded text-sm w-32"
                              value={Math.max(0, displayableImages.findIndex((img) => (img?.id === p.image?.id) || (img?.image_url === p.image?.image_url) || (img?.url === p.image?.url)))}
                              onChange={(e) => {
                                const i = parseInt(e.target.value, 10);
                                const img = displayableImages[i];
                                if (img) setPlacements((prev) => prev.map((pl, j) => (j === idx ? { ...pl, image: img } : pl)));
                              }}
                              disabled={displayableImages.length <= 1}
                            >
                              {displayableImages.map((img, i) => (
                                <option key={i} value={i}>{(img?.prompt || img?.style || 'Image')?.slice(0, 20)}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => setPlacements((prev) => prev.filter((_, i) => i !== idx))}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              aria-label="Remove placement"
                              disabled={placements.length <= 1}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        ))}
                        {availablePlacements.some((ap) => !placements.some((p) => p.placement === ap.type)) && (
                          <button
                            type="button"
                            onClick={() => {
                              const unused = availablePlacements.find((ap) => !placements.some((p) => p.placement === ap.type));
                              if (unused) setPlacements((prev) => [...prev, { placement: unused.type, image: placements[0]?.image || currentPrintImage }]);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            + Add another placement (e.g. back, sleeve)
                          </button>
                        )}
                      </div>
                    ) : (
                      <select
                        id="imagePlacement"
                        className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                        value={selectedPlacement}
                        onChange={(e) => setSelectedPlacement(e.target.value)}
                        disabled={!selectedSize || !selectedColorName}
                      >
                        <option value="">Select placement</option>
                        {availablePlacements.map(placement => (
                          <option key={placement.type} value={placement.type}>{placement.title}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Dynamic Placement Inputs / Thread Colors */}
                {selectedPlacement && selectedPlacement.startsWith('embroidery_') && (
                  <div className="mb-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
                    <h4 className="text-md font-semibold text-blue-800 mb-2">Embroidery Color Selection</h4>
                    <p className="text-sm text-blue-700">
                      You've selected an embroidery option. The thread color will be automatically chosen based on the colors in your image for the best result.
                    </p>
                  </div>
                )}

              </>
            )}
            <div className="mb-4">
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">Quantity:</label>
              <input
                type="number"
                id="quantity"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                min="1"
              />
            </div>
          </div>

          {/* Column 2: Shipping Address */}
          <div>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-1">Ship to</p>
              <p className="text-xs text-gray-500 mb-2">No account needed. We only need a valid address to ship and your email for the order confirmation.</p>
              {savedAddresses.length > 0 ? (
                <div className="space-y-2">
                  <select
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    value={selectedAddressIndex !== null && selectedAddressIndex < savedAddresses.length ? String(selectedAddressIndex) : ""}
                    onChange={(e) => {
                      const idx = e.target.value;
                      if (idx !== "") {
                        const i = parseInt(idx, 10);
                        applySavedAddress(savedAddresses[i], i);
                      } else {
                        setSelectedAddressIndex(null);
                      }
                    }}
                  >
                    <option value="">Select saved address or enter below...</option>
                    {savedAddresses.map((addr, i) => (
                      <option key={addr.id || i} value={i}>
                        {addr.label || `Address ${i + 1}`}: {addr.address1}, {addr.city}, {addr.country_code}
                      </option>
                    ))}
                  </select>
                  {selectedAddressIndex !== null && selectedAddressIndex < savedAddresses.length && (
                    <p className="text-xs text-green-600">Shipping to: {savedAddresses[selectedAddressIndex]?.address1}, {savedAddresses[selectedAddressIndex]?.city}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 mb-2">Enter the address Printful should ship to.</p>
              )}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={handleDetectLocation}
                  className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200"
                >
                  Detect my location
                </button>
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="shippingFirstName" className="block text-sm font-medium text-gray-700 mb-1">First Name:</label>
              <input
                type="text"
                id="shippingFirstName"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={shippingFirstName}
                onChange={(e) => setShippingFirstName(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="shippingLastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name:</label>
              <input
                type="text"
                id="shippingLastName"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={shippingLastName}
                onChange={(e) => setShippingLastName(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="shippingAddress1" className="block text-sm font-medium text-gray-700 mb-1">Address Line 1:</label>
              <input
                type="text"
                id="shippingAddress1"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={shippingAddress1}
                onChange={(e) => setShippingAddress1(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="shippingCity" className="block text-sm font-medium text-gray-700 mb-1">City:</label>
              <input
                type="text"
                id="shippingCity"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={shippingCity}
                onChange={(e) => setShippingCity(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="shippingZip" className="block text-sm font-medium text-gray-700 mb-1">Zip Code:</label>
              <input
                type="text"
                id="shippingZip"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={shippingZip}
                onChange={(e) => setShippingZip(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="shippingCountryCode" className="block text-sm font-medium text-gray-700 mb-1">Country Code:</label>
              <input
                type="text"
                id="shippingCountryCode"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={shippingCountryCode}
                onChange={(e) => setShippingCountryCode(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="shippingEmail" className="block text-sm font-medium text-gray-700 mb-1">Email (required — we send your order here):</label>
              <input
                type="email"
                id="shippingEmail"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={shippingEmail}
                onChange={(e) => {
                  setShippingEmail(e.target.value);
                  persistGuestEmail(e.target.value);
                }}
                required
              />
            </div>
            <div className="mb-4">
              <label htmlFor="shippingPhone" className="block text-sm font-medium text-gray-700 mb-1">Phone:</label>
              <input
                type="tel"
                id="shippingPhone"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={shippingPhone}
                onChange={(e) => setShippingPhone(e.target.value)}
                required
              />
            </div>
            <div className="mb-4">
              <label htmlFor="shippingState" className="block text-sm font-medium text-gray-700 mb-1">State:</label>
              <input
                type="text"
                id="shippingState"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                value={shippingState}
                onChange={(e) => setShippingState(e.target.value)}
              />
            </div>
            <label className="flex items-center gap-2 mt-4 cursor-pointer">
              <input
                type="checkbox"
                checked={saveAddressToProfile}
                onChange={(e) => setSaveAddressToProfile(e.target.checked)}
                className="rounded border-gray-300 text-primary focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Save this address to my profile for future orders</span>
            </label>
          </div>
        </div>

        {Number.isFinite(displayPrice) && displayPrice > 0 ? (
          <p className="text-gray-800 font-bold mb-4 tour-estimated-cost">Estimated cost: ${displayPrice.toFixed(2)}</p>
        ) : (
          <p className="text-sm text-gray-600 mb-4 tour-estimated-cost">
            Estimated cost often updates after the print provider finishes generating the mockup.
          </p>
        )}

        {/* Conditional Buttons */}
        {!generatedMockupId ? (
          <>
            {cannotCreateMockup && (
              <p className="text-amber-700 text-sm font-medium mb-2 p-3 bg-amber-50 border border-amber-200 rounded">
                This image was not fully uploaded. Go back to Design, wait for the upload to finish, then click &quot;Use This Image&quot; before choosing a product.
              </p>
            )}
            {detailedProductData?.is_discontinued && (
              <p className="text-red-600 text-sm font-medium mb-2">This product is discontinued. Mockups are no longer available.</p>
            )}
            <button
              onClick={handleGenerateMockup}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-md transition-colors mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || cannotCreateMockup || detailedProductData?.is_discontinued}
            >
              {loading ? 'Generating Mockup...' : 'Generate Mockup'}
            </button>
          </>
        ) : (
          displayPrice > 0 && (
            <button
              onClick={handleProceedToPayment}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition-colors mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !selectedModel || !selectedSize || !selectedColorName || !shippingAddress1 || !shippingCity || !shippingZip || !shippingCountryCode || !shippingState || !selectedPlacement}
            >
              Proceed to Payment (${(displayPrice * quantity).toFixed(2)})
            </button>
          )
        )}

      </div>

      {message && <p className="mt-4 text-sm text-center text-gray-600">{message}</p>}

    </div>
  );
};

export default PrintOptions;